import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCInput } from 'rollup-allspark';
import ComponentPlaceholder from '../../components/ComponentPlaceholder';
import { cn, isEmpty } from '../../utils';
import { updateEntity } from './actions';
import { makeSelectProperty } from './selector';

const List = ({
  isLoadingProfiles,
  profiles,
  selectedProfile,
  updateField,
}) => {
  const [searchText, setSearchText] = useState('');
  const [filteredProfiles, setFilteredProfiles] = useState([]);

  useEffect(() => {
    if (isLoadingProfiles) return;

    if (isEmpty(profiles)) {
      setFilteredProfiles([]);
      return;
    }

    const trimmedSearchText = searchText.trim().toLowerCase();

    if (!isEmpty(trimmedSearchText)) {
      const filtered = profiles.filter(pp =>
        pp.profile_name.toLowerCase().includes(trimmedSearchText),
      );
      setFilteredProfiles(filtered);
    } else {
      setFilteredProfiles(profiles);
    }
  }, [profiles, searchText]);

  const onProfileClicked = pp => {
    updateField({
      categories: [],
      checkedCategories: [],
      checkedItems: [],
      items: [],
      itemSearchKey: '',
      menus: [],
      selectedBrand: null,
      selectedFilterCategory: null,
      selectedFilterMenu: null,
      selectedProfile: pp,
    });
  };

  const listEl = (() => {
    if (isEmpty(profiles)) {
      return (
        <ComponentPlaceholder label="No printer found for this location" />
      );
    }

    if (isEmpty(filteredProfiles)) {
      return (
        <ComponentPlaceholder label="No printer found for these search parameters" />
      );
    }

    if (isLoadingProfiles) {
      return Array.from({ length: 3 }, (_, i) => (
        <div
          key={i}
          className="flex px-4 py-5 rounded-lg border cursor-pointer gap-x-2 border-grey"
        >
          <div className="w-full flex justify-between">
            <div className="flex flex-1 flex-col gap-y-2">
              <div className="w-3/4 h-6 bg-grey-light rounded-sm" />
              <div className="w-2/3 h-4 bg-grey-light rounded-sm" />
              <div className="w-1/3 h-4 bg-grey-light rounded-sm" />
            </div>
            <div className="flex flex-col justify-between gap-y-2">
              <div className="flex gap-x-2 justify-end">
                <div className="w-6 h-6 bg-grey-light rounded-sm" />
                <div className="w-6 h-6 bg-grey-light rounded-sm" />
              </div>
              <div className="w-20 h-4 bg-grey-light rounded-sm" />
            </div>
          </div>
        </div>
      ));
    }

    return filteredProfiles.map(pp => {
      const matchesSelected =
        !isEmpty(selectedProfile) && pp.id === selectedProfile.id;

      const isKitchenEl =
        pp.docket_kitchen_copy_enabled && pp.docket_kitchen_copy_count > 0 ? (
          <div className="flex items-center justify-center bg-grey-light w-6 h-6 rounded-full">
            <i className="kt-chef-hat text-base" />
          </div>
        ) : null;

      const isCustomerEl =
        pp.docket_customer_copy_enabled && pp.docket_customer_copy_count > 0 ? (
          <div className="flex items-center justify-center bg-grey-light w-6 h-6 rounded-full">
            <i className="kt-people text-base" />
          </div>
        ) : null;

      const isDefaultEl = pp.is_default ? (
        <div className="flex items-center justify-center bg-primary px-2 py-1 rounded-lg text-white text-mini">
          Default
        </div>
      ) : null;

      const propertyEl = (() => {
        const properties = [];

        if (pp.sticker_printer_enabled) {
          properties.push('Sticker');
        }

        if (!isEmpty(pp.printerType)) {
          properties.push(pp.printerType.title);
        }

        if (!isEmpty(pp.printerChildType)) {
          properties.push(pp.printerChildType.title);
        }

        return properties.join(' • ');
      })();

      return (
        <div
          key={pp.id}
          className={cn({
            'flex px-4 py-5 rounded-lg border cursor-pointer gap-x-2': true,
            'border-primary': matchesSelected,
            'border-grey': !matchesSelected,
          })}
          role="presentation"
          onClick={() => onProfileClicked(pp)}
        >
          <div className="w-full flex justify-between">
            <div className="flex flex-1 flex-col gap-y-2">
              <div className="font-medium">{pp.profile_name}</div>
              <div className="text-xs text-grey-darker">{pp.device_name}</div>
              <div className="text-xs text-grey-darker">{propertyEl}</div>
            </div>
            <div className="flex flex-col justify-between gap-y-2">
              <div className="flex gap-x-2 justify-end">
                {isCustomerEl}
                {isKitchenEl}
              </div>
              {isDefaultEl}
            </div>
          </div>
        </div>
      );
    });
  })();

  return (
    <div className="w-1/3">
      <div className="flex flex-col bg-white p-4 rounded-lg border border-grey gap-y-4">
        <div className="text-base flex items-center">Printer List</div>
        <SCInput
          type="text"
          placeholder="Search Printer"
          inputClass="border border-grey rounded-lg"
          value={searchText}
          handleChange={e => setSearchText(e.target.value)}
        />
        <div className="flex flex-col gap-y-4">{listEl}</div>
      </div>
    </div>
  );
};

List.propTypes = {
  isLoadingProfiles: PropTypes.bool,
  profiles: PropTypes.array,
  selectedProfile: PropTypes.object,
  updateField: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  isLoadingProfiles: makeSelectProperty('isLoadingProfiles'),
  profiles: makeSelectProperty('profiles'),
  selectedProfile: makeSelectProperty('selectedProfile'),
});

const mapDispatchToProps = dispatch => ({
  updateField: payload => dispatch(updateEntity(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(List);
